import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { servicesQuery } from '@/services/api';
import { toFormData } from '@/features/business-profile-forms';
import type { AIContextOverrides, AIContext } from '@/pages/journeys/settings/ai-business-profile/types';
import { isEmptyString } from '@/utils/string';
import type { Workspace } from '../types';

interface AIProfileData {
  body: { ai_context: AIContext; overrides: AIContextOverrides };
  workspaceId: number;
}

interface UseAIProfileSelectionResult {
  selectedProfile: AIProfileData | null;
  aiContext: AIContext | null | undefined;
  aiFormData: ReturnType<typeof toFormData> | null;
  isLoading: boolean;
  error: unknown;
}

export function useAIProfileSelection(
  workspaces: Workspace[] | null,
  currentWorkspaceId?: number | null,
): UseAIProfileSelectionResult {
  const {
    data: selectedProfile = null,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['aiProfileSelection', workspaces?.map((w) => w.id), currentWorkspaceId],
    queryFn: async (): Promise<AIProfileData | null> => {
      if (workspaces == null || workspaces.length === 0) {
        return null;
      }

      const profilePromises = workspaces.map(async (workspace) => {
        try {
          const workspaceId = Number.parseInt(workspace.id, 10);
          if (Number.isNaN(workspaceId) || workspaceId <= 0) {
            return null;
          }

          const response = await servicesQuery.getAIContextOverrides.query({
            params: { workspaceId },
          });

          if (response?.body != null) {
            return { body: response.body, workspaceId };
          }
          return null;
        } catch {
          return null;
        }
      });

      const profiles = (await Promise.all(profilePromises)).filter((p): p is AIProfileData => p !== null);

      if (profiles.length === 0) {
        return null;
      }

      if (profiles.length === 1) {
        return profiles[0];
      }

      const domainGroups = new Map<string, AIProfileData[]>();
      for (const profile of profiles) {
        const domain = profile.body.ai_context?.domain;
        if (domain != null && !isEmptyString(domain)) {
          const existing = domainGroups.get(domain);
          if (existing == null) {
            domainGroups.set(domain, [profile]);
          } else {
            existing.push(profile);
          }
        }
      }

      if (domainGroups.size === 1) {
        const profilesWithSameDomain = Array.from(domainGroups.values())[0];
        if (profilesWithSameDomain.length === profiles.length) {
          const latest = profilesWithSameDomain.reduce((latestProfile, current) => {
            const latestDate = new Date(latestProfile.body.ai_context?.updated_at ?? 0);
            const currentDate = new Date(current.body.ai_context?.updated_at ?? 0);
            return currentDate > latestDate ? current : latestProfile;
          });
          return latest;
        }
      }

      const preferredWorkspaceId = currentWorkspaceId ?? Number.parseInt(workspaces[0].id, 10);
      const preferredProfile = profiles.find((p) => p.workspaceId === preferredWorkspaceId);
      if (preferredProfile != null) {
        return preferredProfile;
      }

      return profiles[0];
    },
    enabled: workspaces != null && workspaces.length > 0,
  });

  const aiContext = selectedProfile?.body?.ai_context;

  const aiFormData = useMemo(() => {
    const profileBody = selectedProfile?.body;
    return profileBody != null ? toFormData(profileBody.ai_context, profileBody.overrides) : null;
  }, [selectedProfile]);

  return {
    selectedProfile,
    aiContext,
    aiFormData,
    isLoading,
    error,
  };
}
